import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Stripe, StripeElements } from '@stripe/stripe-js'

import AppLayout from '../../../components/app-layout/AppLayout'
import CardSurface from '../../../components/card-surface/CardSurface'
import Button from '../../../components/button/Button'
import { useResidentTenancy } from '../../../_hooks/use-resident-tenancy'
import {
  createRentCheckout,
  createRentPaymentIntent,
  createChargePaymentIntent,
} from '../../../_data/resident'
import { ResidentPaymentMethodKind } from '../../../_types/resident'
import { getStripeClient } from '../../../_utilities/stripe-loader'
import { formatCents } from '../../../_utilities/resident-format'
import { PAYMENT_ELEMENT_APPEARANCE } from '../../../_defaults/payment-element-appearance'
import { PayBreakdown, PayRentPageState } from './types'
import {
  PayStack,
  PayHeader,
  PayTitle,
  PaySubtitle,
  BreakdownList,
  BreakdownRow,
  BreakdownLabel,
  BreakdownValue,
  ElementContainer,
  PayActions,
  PayErrorText,
  QuietNote,
  BackLink,
} from './styled'

/**
 * The in-app rent payment page: the fee-disclosure breakdown (rent, any card
 * processing fee, and the total) above an embedded Stripe Payment Element, so
 * the renter pays without leaving QuickPass. The amount comes from a fresh
 * server-created PaymentIntent per visit — never from the client, and never a
 * reused intent. When the intent, Stripe.js, or the publishable key is
 * unavailable, the page silently falls back to the hosted Stripe Checkout so
 * payment always has a working path.
 *
 * @returns {JSX.Element}
 */
const PayRentPage = (): JSX.Element => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { tenancies, isLoadingTenancies } = useResidentTenancy()

  const tenancyId = params.tenancyId ?? ''
  const paymentMethodKind: ResidentPaymentMethodKind =
    searchParams.get('method') === 'card' ? 'card' : 'bank'
  const chargeId = searchParams.get('charge') ?? ''

  const [pageState, setPageState] = useState<PayRentPageState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [breakdown, setBreakdown] = useState<PayBreakdown | null>(null)
  const stripeRef = useRef<Stripe | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const elementContainerRef = useRef<HTMLDivElement | null>(null)

  const tenancy =
    tenancies.find(candidate => candidate.id === tenancyId) ?? null

  useEffect(() => {
    if (tenancyId === '') {
      navigate('/payments')
      return
    }
    if (isLoadingTenancies === false && tenancy === null) {
      navigate('/payments')
    }
  }, [tenancyId, isLoadingTenancies, tenancy, navigate])

  useEffect(() => {
    if (tenancyId === '') {
      return undefined
    }

    let isActive = true

    const fallBackToHostedCheckout = async (): Promise<void> => {
      const checkout = await createRentCheckout(tenancyId, paymentMethodKind)
      if (isActive === false) {
        return
      }
      if (checkout === null) {
        setPageState('error')
        setErrorMessage('We could not start the payment. Please try again.')
        return
      }
      window.location.href = checkout.checkoutUrl
    }

    // One charge and the whole balance are priced by different endpoints, and
    // both are normalised to a PayBreakdown here so everything below this line
    // renders identically. The fee disclosure is a compliance surface, so it
    // must not exist in two versions that can drift apart.
    const loadBreakdown = async (): Promise<PayBreakdown | null> => {
      if (chargeId !== '') {
        const chargeResult = await createChargePaymentIntent(
          tenancyId,
          chargeId,
          paymentMethodKind,
        )
        if (chargeResult === null) {
          return null
        }
        return {
          clientSecret: chargeResult.clientSecret,
          label: chargeResult.chargeDescription,
          amountCents: chargeResult.chargeCents,
          feeCents: chargeResult.feeCents,
          totalCents: chargeResult.totalCents,
        }
      }

      const rentResult = await createRentPaymentIntent(
        tenancyId,
        paymentMethodKind,
      )
      if (rentResult === null) {
        return null
      }
      return {
        clientSecret: rentResult.clientSecret,
        label: 'Rent',
        amountCents: rentResult.rentCents,
        feeCents: rentResult.feeCents,
        totalCents: rentResult.totalCents,
      }
    }

    const setUpPayment = async (): Promise<void> => {
      const [intentResult, stripe] = await Promise.all([
        loadBreakdown(),
        getStripeClient(),
      ])
      if (isActive === false) {
        return
      }

      if (
        intentResult === null ||
        stripe === null ||
        elementContainerRef.current === null
      ) {
        // Only a whole-balance payment can fall back to hosted Checkout. No
        // hosted flow can price a single charge, so falling back on the charge
        // path would quietly charge the entire balance instead of the one line
        // the resident tapped.
        if (chargeId === '') {
          await fallBackToHostedCheckout()
          return
        }
        setPageState('error')
        setErrorMessage(
          'We could not start this payment. Please go back and try again.',
        )
        return
      }

      setBreakdown(intentResult)
      stripeRef.current = stripe

      const elements = stripe.elements({
        clientSecret: intentResult.clientSecret,
        appearance: PAYMENT_ELEMENT_APPEARANCE,
      })
      elementsRef.current = elements

      const paymentElement = elements.create('payment')
      paymentElement.mount(elementContainerRef.current)
      setPageState('ready')
    }

    void setUpPayment()

    return () => {
      isActive = false
      const elements = elementsRef.current
      if (elements !== null) {
        const paymentElement = elements.getElement('payment')
        if (paymentElement !== null) {
          paymentElement.destroy()
        }
      }
      elementsRef.current = null
      stripeRef.current = null
    }
  }, [tenancyId, paymentMethodKind, chargeId])

  const handleConfirm = useCallback(async (): Promise<void> => {
    const stripe = stripeRef.current
    const elements = elementsRef.current
    if (stripe === null || elements === null) {
      return
    }

    setErrorMessage('')
    setPageState('confirming')

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/#/payments?payment=success`,
      },
      redirect: 'if_required',
    })

    if (result.error !== undefined) {
      setErrorMessage(
        result.error.message ??
          'Your payment could not be completed. Please try again.',
      )
      setPageState('ready')
      return
    }

    navigate('/payments?payment=success')
  }, [navigate])

  const isSingleCharge = chargeId !== ''
  const methodWord = paymentMethodKind === 'card' ? 'card' : 'bank'

  let methodTitle = `Pay rent by ${methodWord}`
  if (isSingleCharge === true) {
    methodTitle = `Pay by ${methodWord}`
  }

  let subtitleText = 'Review your payment, then confirm below.'
  if (tenancy !== null) {
    subtitleText = `Review your payment for ${tenancy.unitLabel}, then confirm below.`
  }

  return (
    <AppLayout contentMaxWidth="standard">
      <PayStack>
        <PayHeader>
          <PayTitle>{methodTitle}</PayTitle>
          <PaySubtitle>{subtitleText}</PaySubtitle>
        </PayHeader>

        <CardSurface padding="standard">
          {breakdown !== null ? (
            <BreakdownList>
              <BreakdownRow>
                <BreakdownLabel>{breakdown.label}</BreakdownLabel>
                <BreakdownValue>
                  {formatCents(breakdown.amountCents)}
                </BreakdownValue>
              </BreakdownRow>
              {breakdown.feeCents > 0 ? (
                <BreakdownRow>
                  <BreakdownLabel>Card processing fee</BreakdownLabel>
                  <BreakdownValue>
                    {formatCents(breakdown.feeCents)}
                  </BreakdownValue>
                </BreakdownRow>
              ) : null}
              <BreakdownRow emphasis="total">
                <BreakdownLabel emphasis="total">Total</BreakdownLabel>
                <BreakdownValue emphasis="total">
                  {formatCents(breakdown.totalCents)}
                </BreakdownValue>
              </BreakdownRow>
            </BreakdownList>
          ) : null}

          {pageState === 'loading' ? (
            <QuietNote>Preparing your payment...</QuietNote>
          ) : null}

          <ElementContainer ref={elementContainerRef} />

          {errorMessage !== '' ? (
            <PayErrorText>{errorMessage}</PayErrorText>
          ) : null}

          {pageState === 'ready' || pageState === 'confirming' ? (
            <PayActions>
              <Button
                type="button"
                variant="primary"
                size="small"
                isLoading={pageState === 'confirming'}
                disabled={pageState === 'confirming'}
                onClick={() => {
                  void handleConfirm()
                }}
              >
                {breakdown !== null
                  ? `Pay ${formatCents(breakdown.totalCents)}`
                  : 'Pay now'}
              </Button>
              <BackLink
                type="button"
                onClick={() => {
                  navigate('/payments')
                }}
              >
                Back to payments
              </BackLink>
            </PayActions>
          ) : null}

          {pageState === 'error' ? (
            <PayActions>
              <BackLink
                type="button"
                onClick={() => {
                  navigate('/payments')
                }}
              >
                Back to payments
              </BackLink>
            </PayActions>
          ) : null}
        </CardSurface>
      </PayStack>
    </AppLayout>
  )
}

export default PayRentPage
